/**
 * Change Password Modal
 * Lets an authenticated user set a new password without using the email reset flow.
 * The user's current Supabase session is already authenticated, so we only ask
 * for the new password twice (to catch typos).
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import LockResetIcon from '@mui/icons-material/LockReset';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './ChangePasswordModal.css';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MIN_LENGTH = 8;

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const { updatePassword } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError(null);

    if (newPassword.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await updatePassword(newPassword);
      if (updateError) {
        setError(updateError.message || 'Could not update password');
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err?.message || 'Unexpected error updating password');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && newPassword && confirmPassword) {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="change-password-overlay" onClick={handleClose}>
      <div className="change-password-modal" onClick={(e) => e.stopPropagation()}>
        <div className="change-password-header">
          <div className="header-title">
            <LockResetIcon />
            <span>Change Password</span>
          </div>
          <button className="close-btn" onClick={handleClose} disabled={loading}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="change-password-content">
          {success ? (
            <div className="success-message">
              <CheckCircleIcon className="success-icon" />
              <h3>Password updated</h3>
              <p>Use your new password the next time you sign in.</p>
              <button className="done-btn" onClick={handleClose}>Done</button>
            </div>
          ) : (
            <>
              <p className="instructions">
                Set a new password for your account. Minimum {MIN_LENGTH} characters.
              </p>

              <div className="input-group">
                <label htmlFor="cp-new">New password</label>
                <input
                  id="cp-new"
                  type={showPassword ? 'text' : 'password'}
                  className="password-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  autoFocus
                  autoComplete="new-password"
                />
              </div>

              <div className="input-group">
                <label htmlFor="cp-confirm">Confirm new password</label>
                <input
                  id="cp-confirm"
                  type={showPassword ? 'text' : 'password'}
                  className="password-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <label className="show-toggle">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  disabled={loading}
                />
                Show password
              </label>

              {error && <div className="error-message">{error}</div>}

              <button
                className="submit-btn"
                onClick={handleSubmit}
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
